import type { Edge } from '@xyflow/react';
import type { OntologyNode, Gene } from '@/types/ontologyGraph';
import type { OntologyFlowNode } from './types';
import {
  isShowMoreGenesNodeId,
  isOtherGenesNodeId,
  isShowRelatedDiseasesNodeId,
  isMoreNodeId,
} from '@/types/ontologyGraph';
import { GENE_NODE_ID_PREFIX } from '@/services/ontologyGraph.service';

type BuildDiseaseGeneTreeParams = {
  nodes: Map<string, OntologyNode>;
  edges: { source: string; target: string }[];
  allGenesForDisease: (diseaseId: string) => Gene[] | undefined;
  onToggle: (nodeId: string) => void;
  onCollapse: (nodeId: string) => void;
  onNavigate: (nodeId: string) => void;
  onGeneClick: (geneNodeId: string) => void;
  onShowRelatedDiseasesClick: (geneNodeId: string) => void;
  onActionClick: (nodeId: string, actionType: string, meta: Record<string, unknown>) => void;
  openMoreList: (moreNodeId: string) => void;
};

export function buildDiseaseGeneTree({
  nodes,
  edges,
  onToggle,
  onCollapse,
  onNavigate,
  onGeneClick,
  onShowRelatedDiseasesClick,
  onActionClick,
  openMoreList,
}: BuildDiseaseGeneTreeParams): { flowNodes: OntologyFlowNode[]; flowEdges: Edge[] } {
  const flowNodes: OntologyFlowNode[] = [];

  const parentById = new Map<string, string>();
  for (const e of edges) {
    parentById.set(e.target, e.source);
  }

  const flowEdges: Edge[] = edges.map(e => ({
    id: `e-${e.source}-${e.target}`,
    source: e.source,
    target: e.target,
    type: 'smoothstep',
    style: { stroke: '#c8d6e5', strokeWidth: 1.5 },
  }));

  for (const [id, node] of nodes) {
    const parentId = parentById.get(id);

    if (node.type === 'gene') {
      const geneId = id.startsWith(GENE_NODE_ID_PREFIX) ? id.slice(GENE_NODE_ID_PREFIX.length) : id;
      const hasRelatedDiseases = node.children.some(childId => {
        const child = nodes.get(childId);
        return child?.type === 'disease';
      });
      flowNodes.push({
        id,
        type: 'gene',
        position: { x: 0, y: 0 },
        data: {
          label: node.label,
          fullName: undefined,
          diseaseId: parentId ?? '',
          geneId,
          hasRelatedDiseases,
          onClick: onGeneClick,
          onCollapse: hasRelatedDiseases ? onCollapse : undefined,
        },
      } as OntologyFlowNode);
    } else if (isMoreNodeId(id)) {
      flowNodes.push({
        id,
        type: 'action',
        position: { x: 0, y: 0 },
        data: {
          label: node.label,
          actionType: 'more_children',
          meta: {},
          onClick: () => openMoreList(id),
        },
      } as OntologyFlowNode);
    } else if (isShowRelatedDiseasesNodeId(id)) {
      const geneNodeId = node.meta?.geneId ? `${GENE_NODE_ID_PREFIX}${node.meta.geneId}` : (parentId ?? id);
      flowNodes.push({
        id,
        type: 'action',
        position: { x: 0, y: 0 },
        data: {
          label: node.label,
          actionType: 'show_related_diseases',
          meta: { geneId: node.meta?.geneId },
          onClick: () => onShowRelatedDiseasesClick(geneNodeId),
        },
      } as OntologyFlowNode);
    } else if (isShowMoreGenesNodeId(id) || isOtherGenesNodeId(id)) {
      const actionType = isShowMoreGenesNodeId(id) ? 'show_more_genes' : 'other_genes';
      flowNodes.push({
        id,
        type: 'action',
        position: { x: 0, y: 0 },
        data: {
          label: node.label,
          actionType,
          meta: { diseaseId: node.meta?.diseaseId, count: node.meta?.count },
          onClick: () => onActionClick(id, actionType, { diseaseId: node.meta?.diseaseId }),
        },
      } as OntologyFlowNode);
    } else {
      flowNodes.push({
        id,
        type: 'disease',
        position: { x: 0, y: 0 },
        data: {
          label: node.label,
          cui: node.cui,
          hasChildren: node.hasChildren,
          isExpanded: node.expanded,
          numDescendants: node.hiddenChildrenCount,
          onToggle,
          onCollapse,
          onNavigate,
        },
      } as OntologyFlowNode);
    }
  }

  return { flowNodes, flowEdges };
}
